import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';

import ChartTableDynamic from '@component/LightVersion/Reports/dynamic';
import {
  colors,
  statusesOnTimeLate,
} from '@component/LightVersion/Reports/dynamic/types';
import { useNavigate } from 'react-router-dom';
import { ReportTitleInfo } from '@component/LightVersion/Reports/ReportTitleInfo';
import { formatMonthLabel } from '@component/LightVersion/Reports/yAxisHelpers';
import BarManyLinesChartSkeleton from '@src/uikit/Loader/DashboardSkeleton/BarManyLinesChartSkeleton';
import ChartTableDynamicSkeleton from '@src/uikit/Loader/DashboardSkeleton/ChartTableDynamicSkeleton';
import { blurStyle } from '@component/LightVersion/Reports/helpers';
import { REPORT_CHART_FONT } from '@component/LightVersion/Reports/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ON_TIME_PERFORMANCE_COLOR = '#1E90FF';
const CONTRACTOR_COMPARISON_COLOR = '#ED6214';
const NETWORK_AVERAGE_COLOR = '#4F7EFF';

interface MonthStat {
  month: string;
  onTimePercentage: number;
  latePercentage: number;
  networkAverage?: number;
}

interface TotalStats {
  onTimePercentage: number;
  totalLate: number;
  totalOnTime: number;
}

interface StatsData {
  onTimePercentageMonthStats: MonthStat[];
  onTimePercentageTotalStats: TotalStats;
}

interface LineChartProps {
  data?: StatsData;
  title: string;
  tooltip?: string;
  navigateUrl?: string;
  navigationState?: unknown;
  loading?: boolean;
  isFetching?: boolean;
  showTable?: boolean;
  hasData?: boolean;
}

export const formattedNumberType = (value: any) =>
  new Intl.NumberFormat('en-US').format(value);

export const formatPercent = (value: any) => {
  const num = Number.parseFloat(value);
  const safe = Number.isNaN(num) ? 0 : num;

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(safe / 100);
};

export const formatThousandToK = (value: number) => {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
  }).format(value);
};

const stepSize = (value: number) => {
  if ([30, 50].includes(value ?? NaN)) return 1;
  if ([0, 10, 20].includes(value ?? NaN)) return 10;
  return 2;
};

const LineChart: React.FC<LineChartProps> = ({
  data,
  title,
  tooltip,
  navigateUrl,
  navigationState,
  loading = false,
  isFetching = false,
  showTable = true,
  hasData = true,
}) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="dashboard-card-container">
        <BarManyLinesChartSkeleton />
        <ChartTableDynamicSkeleton />
      </div>
    );
  }

  const monthStats = data?.onTimePercentageMonthStats || [];

  const rawLabels = monthStats.map((stat) => stat.month);
  const formattedLabels = rawLabels.map((value) => formatMonthLabel(value));
  const values = monthStats.map((stat) => stat.onTimePercentage);
  const networkAverageValues = monthStats.map(
    (stat) => stat.networkAverage ?? null
  );
  const hasNetworkAverage = networkAverageValues.some(
    (value) => value !== null
  );
  const yMax = 100;

  const scaleValues = [
    ...values,
    ...networkAverageValues.filter((value): value is number => value !== null),
  ];
  const totalOtp =
    scaleValues.length > 1 ? Math.floor(Math.min(...scaleValues)) : 0;

  const yMin = Number(
    Math.floor(
      Math.floor(totalOtp!) / Math.pow(10, String(totalOtp).length - 1)
    ) + '0'
  );

  const benchmarkValue = (yMax + yMin) / 2;
  // const stepSizeValue = [20, 30, 50].includes(totalOtp ?? NaN) ? 1 : 2;
  const stepSizeValue = stepSize(totalOtp);
  const datasets: ChartData<'line'>['datasets'] = [
    {
      label: 'On Time Performance',
      data: values,
      borderColor: CONTRACTOR_COMPARISON_COLOR,
      backgroundColor: CONTRACTOR_COMPARISON_COLOR,
      fill: false,
      tension: 0.3,
      clip: 10,
    },
  ];

  if (hasNetworkAverage) {
    datasets.unshift({
      label: 'Network Average',
      data: networkAverageValues,
      borderColor: NETWORK_AVERAGE_COLOR,
      backgroundColor: NETWORK_AVERAGE_COLOR,
      fill: false,
      tension: 0.3,
      clip: 10,
    });
  }

  const chartData: ChartData<'line'> = {
    labels: formattedLabels,
    datasets,
  };

  const chartOptions: ChartOptions<'line'> = {
    scales: {
      x: {
        type: 'category',
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          font: REPORT_CHART_FONT,
          color: '#FAFAFBFF',
          callback: (_value, index) => formattedLabels[index] ?? '',
        },
      },
      y: {
        beginAtZero: false,
        min: yMin,
        max: yMax,
        ticks: {
          stepSize: stepSizeValue,
          color: '#FAFAFBFF',
          callback: (value) => `${value}%`,
        },
      },
    },
    layout: {
      padding: {
        top: 6,
        bottom: 6,
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: 'bottom',
        labels: {
          color: '#FAFAFBFF',
          font: REPORT_CHART_FONT,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#333',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          title: (items) => {
            const idx = items?.[0]?.dataIndex ?? 0;
            return formattedLabels[idx] ?? '';
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const tableData =
    monthStats.length > 0
      ? monthStats.map((m, index) => ({
          month: formattedLabels[index] ?? m.month,
          onTime: {
            count: `${m.onTimePercentage ?? 0}%`,
            percentage: m.onTimePercentage ?? 0,
            increase: false,
          },
          late: {
            count: `${m.latePercentage ?? 0}%`,
            percentage: m.latePercentage ?? 0,
            increase: false,
          },
        }))
      : [];

  const handleNavigateClick = () => {
    if (navigateUrl) {
      navigate(navigateUrl, { state: navigationState });
    }
  };

  return (
    <>
      <div
        data-test-id="c-index-1pyt"
        className="line-container"
        style={{
          padding: '20px',
          position: 'relative',
          ...blurStyle(isFetching),
        }}
      >
        <button
          className="report-view-data"
          onClick={handleNavigateClick}
          data-test-id="c-index-1pyt-view-data"
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          View Data
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div
            data-test-id="c-index-1pyt-header"
            style={{
              color: '#fff',
              fontSize: 14,
              marginBottom: 30,
            }}
          >
            <ReportTitleInfo title={title} tooltip={tooltip} />
          </div>
          <div
            style={{ height: '400px', width: '100%' }}
            data-test-id="c-index-1pyt-line-container"
          >
            {hasData ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="line-chart-no-data no-data-found">
                No Data Found
              </div>
            )}
          </div>
        </div>

        {hasData && (
          <div
            className="block-container"
            style={{ marginLeft: '20px' }}
            data-test-id="c-index-1pyt-block-container"
          >
            <div
              className="stats-card"
              style={{ width: '186px' }}
              data-test-id="c-index-1pyt-stats-card-total-on-time"
            >
              <div className="stats-label">Total On Time</div>
              <div
                className="stats-value"
                data-test-id="c-index-1pyt-stats-value-total-on-time"
              >
                {formattedNumberType(
                  data?.onTimePercentageTotalStats?.totalOnTime ?? 0
                )}
              </div>
            </div>

            <div
              className="stats-card"
              data-test-id="c-index-1pyt-stats-card-total-late"
            >
              <div className="stats-label">Total Late</div>
              <div
                className="stats-value"
                data-test-id="c-index-1pyt-stats-value-total-late"
              >
                {formattedNumberType(
                  data?.onTimePercentageTotalStats?.totalLate ?? 0
                )}
              </div>
            </div>

            <div
              className="stats-card"
              data-test-id="c-index-1pyt-stats-card-total-otp"
            >
              <div className="stats-label">Total OTP</div>
              <div
                className="stats-value"
                data-test-id="c-index-1pyt-stats-value-total-otp"
              >
                {formatPercent(
                  data?.onTimePercentageTotalStats?.onTimePercentage
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {hasData && showTable && tableData.length > 0 && (
        <ChartTableDynamic
          dataTestId="c-index-1pyt-chart-table-dynamic"
          data={tableData as any}
          colors={{ ...colors, onTime: ON_TIME_PERFORMANCE_COLOR }}
          statuses={statusesOnTimeLate}
          hasArrowIncDec={false}
          isFetching={isFetching}
        />
      )}
    </>
  );
};

export default LineChart;
